import PropTypes from 'prop-types';
import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const ResetClockConfirmDialog = ({ open, onClose, onConfirm }) => {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    const trimmedText = confirmText.trim();
    if (trimmedText.toLowerCase() !== 'reset') {
      setError('Please type "reset" or "RESET" to confirm');
      return;
    }
    
    onConfirm();
    setConfirmText('');
    setError('');
  };

  const handleClose = () => {
    setConfirmText('');
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1E1E1E',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: '#FFFFFF',
          fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
          fontSize: 20,
          fontWeight: 600,
          pb: 1,
        }}
      >
        Confirm Reset
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
              fontSize: 14,
              mb: 2,
            }}
          >
            Are you sure you want to reset?
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
              fontSize: 12,
              mb: 2,
            }}
          >
            Type "reset" or "RESET" to confirm:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Type 'reset' to confirm"
            value={confirmText}
            onChange={(e) => {
              setConfirmText(e.target.value);
              setError('');
            }}
            error={!!error}
            helperText={error || 'Enter "reset" or "RESET" to proceed'}
            sx={{
              '& .MuiInputBase-input': {
                color: '#FFFFFF',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#45FFD5',
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#45FFD5',
                },
              },
              '& .MuiFormHelperText-root': {
                color: error ? '#D1030D' : 'rgba(255, 255, 255, 0.6)',
              },
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirm();
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          sx={{
            color: '#FFFFFF',
            fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={confirmText.trim().toLowerCase() !== 'reset'}
          sx={{
            backgroundColor: confirmText.trim().toLowerCase() === 'reset' ? '#D1030D' : 'rgba(209, 3, 13, 0.5)',
            color: '#FFFFFF',
            fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: confirmText.trim().toLowerCase() === 'reset' ? '#B0020A' : 'rgba(209, 3, 13, 0.5)',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(209, 3, 13, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          Reset Match
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ResetClockConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ResetClockConfirmDialog;
